/**
 * Sprint Service
 * AIDLC Administrative Backend
 *
 * API calls for sprint CRUD, board data, and burndown operations.
 */

import apiClient from './api';
import type { Sprint, SprintBoardData, BurndownData } from '../types/projects';

const SPRINTS_PATH = '/sprints';

/**
 * Fetch all sprints for a project
 */
export async function fetchSprints(projectId: string): Promise<Sprint[]> {
  const response = await apiClient.get<Sprint[]>(`${SPRINTS_PATH}?projectId=${encodeURIComponent(projectId)}`);
  return response.data;
}

/**
 * Create a new sprint
 */
export async function createSprint(data: {
  projectId: string;
  name: string;
  goal: string;
  startDate: string;
  endDate: string;
  backlogItemIds?: string[];
}): Promise<Sprint> {
  const response = await apiClient.post<Sprint>(SPRINTS_PATH, data);
  return response.data;
}

/**
 * Get a single sprint by ID
 */
export async function getSprint(id: string): Promise<Sprint> {
  const response = await apiClient.get<Sprint>(`${SPRINTS_PATH}/${id}`);
  return response.data;
}

/**
 * Update sprint details (name, goal, dates)
 */
export async function updateSprint(id: string, data: Partial<Pick<Sprint, 'name' | 'goal' | 'startDate' | 'endDate'>>): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}`, data);
  return response.data;
}

/**
 * Activate a sprint (moves from planning -> active)
 */
export async function activateSprint(id: string): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}/activate`, {});
  return response.data;
}

/**
 * Complete a sprint (moves from active -> completed, calculates velocity)
 */
export async function completeSprint(id: string, retrospective?: {
  whatWentWell: string[];
  whatToImprove: string[];
  actionItems: string[];
}): Promise<Sprint> {
  const response = await apiClient.put<Sprint>(`${SPRINTS_PATH}/${id}/complete`, { retrospective });
  return response.data;
}

/* ── Sprint economics: estimate against actual ───────────────────────────── */

export interface RollupSpend {
  inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheCreationTokens: number;
  totalTokens: number; costUsd: number; sessions: number;
}
export interface RollupEstimated {
  items: number; inputTokens: number; outputTokens: number; totalTokens: number; costUsd: number;
}
export interface SprintItemRollup {
  id: string; title: string; status: string; assignedAgent: string | null;
  hasEstimate: boolean; estimated: RollupEstimated; actual: RollupSpend;
  /** null where there is no estimate: there is nothing to vary from. */
  varianceUsd: number | null; lastRunAt: string | null;
}
export type VarianceStanding = 'nothing-estimated' | 'no-spend' | 'too-thin' | 'sound';

/** The coverage below which the rollup refuses to stand behind a variance. Kept
 *  in step with COVERAGE_FLOOR_PCT in server/src/services/cost-rollup.ts, which
 *  is the authority - this copy only decides what the screen emphasises. */
export const COVERAGE_FLOOR_PCT = 50;

export interface SprintRollup {
  sprintId: string;
  /** The project the sprint belongs to, so a project-scoped screen can tell. */
  projectId: string;
  name: string; status: string; startDate: string; endDate: string;
  items: SprintItemRollup[];
  estimated: RollupEstimated; actual: RollupSpend; actualOnEstimated: RollupSpend;
  varianceUsd: number | null;
  /** Whether varianceUsd is worth stating. Decided by the rollup, beside the
   *  spend it is drawn from - not re-derived here. */
  varianceStanding: VarianceStanding;
  coverage: {
    itemsInSprint: number; itemsMissing: number; itemsEstimated: number;
    /** Spend anywhere in the sprint. Never state this about the variance. */
    itemsWithSpend: number;
    /** Estimated items with a recorded run - the variance's own evidence. */
    itemsEstimatedWithSpend: number;
    estimateCoveragePct: number; varianceIsPartial: boolean; unestimatedSpendUsd: number;
  };
}

/**
 * Estimate against actual, newest start first. Scoped to one project when a
 * projectId is given - which a project-scoped screen must do, or it reports
 * another project's sprints as this project's (Bill).
 */
export async function getAllSprintEconomics(projectId?: string): Promise<SprintRollup[]> {
  const path = projectId
    ? `${SPRINTS_PATH}/economics?projectId=${encodeURIComponent(projectId)}`
    : `${SPRINTS_PATH}/economics`;
  const response = await apiClient.get<SprintRollup[]>(path);
  return response.data;
}

/** The same for one sprint. */
export async function getSprintEconomics(sprintId: string): Promise<SprintRollup> {
  const response = await apiClient.get<SprintRollup>(`${SPRINTS_PATH}/${sprintId}/economics`);
  return response.data;
}

/** Every sprint, across projects. */
export async function fetchAllSprints(): Promise<Sprint[]> {
  const response = await apiClient.get<Sprint[]>(SPRINTS_PATH);
  return response.data;
}

/**
 * Add backlog items to a sprint
 */
export async function addItemsToSprint(sprintId: string, itemIds: string[]): Promise<Sprint> {
  const response = await apiClient.post<Sprint>(`${SPRINTS_PATH}/${sprintId}/items`, { itemIds });
  return response.data;
}

/**
 * Remove a single item from a sprint
 */
export async function removeItemFromSprint(sprintId: string, itemId: string): Promise<void> {
  await apiClient.delete(`${SPRINTS_PATH}/${sprintId}/items/${itemId}`);
}

/**
 * Get Kanban board data for a sprint
 */
export async function getSprintBoard(sprintId: string): Promise<SprintBoardData> {
  const response = await apiClient.get<SprintBoardData>(`${SPRINTS_PATH}/${sprintId}/board`);
  return response.data;
}

/**
 * Get burndown chart data for a sprint
 */
export async function getSprintBurndown(sprintId: string): Promise<BurndownData> {
  const response = await apiClient.get<BurndownData>(`${SPRINTS_PATH}/${sprintId}/burndown`);
  return response.data;
}
